import { sanitizeHtml } from './sanitize.js';
import type { FieldType, FileValue, ImageValue, LinkValue } from './types.js';
import { isSafeUrl } from './url.js';

export interface ApplyContext {
  /** BCP-47 tag used for `Intl` formatting. */
  locale: string;
}

export type ApplyOutcome = 'applied' | 'skipped';

const ATTR = 'data-siteflow-attr';
const STATE = 'data-siteflow-state';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Resolves `a.b.c` against a plain object. Returns undefined on any miss. */
export function getPath(source: unknown, path: string): unknown {
  let current: unknown = source;
  for (const segment of path.split('.')) {
    if (!isRecord(current)) return undefined;
    current = current[segment];
  }
  return current;
}

function tagOf(el: Element): string {
  return el.tagName.toLowerCase();
}

function replaceContent(el: Element, fragment: DocumentFragment): void {
  // `replaceChildren` is unavailable on pre-2020 Safari; wiping the element by
  // hand there is far better than leaving a customer's page half-rendered.
  if (typeof el.replaceChildren === 'function') {
    el.replaceChildren(fragment);
    return;
  }
  while (el.firstChild) el.removeChild(el.firstChild);
  el.appendChild(fragment);
}

/**
 * Writes a plain string. Never touches innerHTML — everything that is not
 * explicitly rich text lands as text or as an attribute value.
 */
function writeScalar(el: Element, text: string): void {
  const attr = el.getAttribute(ATTR);
  if (attr) {
    el.setAttribute(attr, text);
    return;
  }

  const tag = tagOf(el);
  if (tag === 'meta') {
    el.setAttribute('content', text);
    return;
  }
  if (tag === 'title') {
    el.ownerDocument.title = text;
    return;
  }
  if (tag === 'input' || tag === 'textarea') {
    (el as HTMLInputElement | HTMLTextAreaElement).value = text;
    return;
  }
  el.textContent = text;
}

function applyDatetime(el: Element, value: unknown, ctx: ApplyContext): ApplyOutcome {
  if (typeof value !== 'string' && typeof value !== 'number') return 'skipped';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'skipped';

  const format = el.getAttribute('data-siteflow-format') ?? 'date';
  const options: Intl.DateTimeFormatOptions =
    format === 'time'
      ? { timeStyle: 'short' }
      : format === 'datetime'
        ? { dateStyle: 'medium', timeStyle: 'short' }
        : { dateStyle: 'medium' };

  writeScalar(el, new Intl.DateTimeFormat(ctx.locale, options).format(date));
  return 'applied';
}

function applyBoolean(el: Element, value: unknown): ApplyOutcome {
  if (typeof value !== 'boolean') return 'skipped';

  const attr = el.getAttribute(ATTR);
  if (attr) {
    if (value) el.setAttribute(attr, '');
    else el.removeAttribute(attr);
    return 'applied';
  }

  const label = el.getAttribute(value ? 'data-siteflow-true' : 'data-siteflow-false');
  if (label === null) return 'skipped';
  el.textContent = label;
  return 'applied';
}

function applyImage(el: Element, value: unknown): ApplyOutcome {
  if (!isRecord(value)) return 'skipped';
  const image = value as unknown as ImageValue;
  if (!isSafeUrl(image.src, { allowData: true })) return 'skipped';

  const tag = tagOf(el);
  if (tag === 'img') {
    el.setAttribute('src', image.src);
    el.setAttribute('alt', image.alt ?? '');
    if (image.width) el.setAttribute('width', String(image.width));
    if (image.height) el.setAttribute('height', String(image.height));
    if (image.srcset) el.setAttribute('srcset', image.srcset);
    return 'applied';
  }
  if (tag === 'meta') {
    el.setAttribute('content', image.src);
    return 'applied';
  }
  if (tag === 'source') {
    el.setAttribute('srcset', image.srcset ?? image.src);
    return 'applied';
  }
  if (el.getAttribute(ATTR) === 'background') {
    // Quotes and backslashes would let a URL escape the CSS string context.
    const safe = image.src.replace(/["\\]/g, '');
    (el as HTMLElement).style.backgroundImage = `url("${safe}")`;
    return 'applied';
  }
  return 'skipped';
}

function applyFile(el: Element, value: unknown): ApplyOutcome {
  if (!isRecord(value)) return 'skipped';
  const file = value as unknown as FileValue;
  if (!isSafeUrl(file.src)) return 'skipped';

  if (tagOf(el) === 'a') {
    el.setAttribute('href', file.src);
    if (el.hasAttribute('data-siteflow-download') && file.filename) {
      el.setAttribute('download', file.filename);
    }
    return 'applied';
  }

  writeScalar(el, file.filename ?? file.src);
  return 'applied';
}

function applyLink(el: Element, value: unknown): ApplyOutcome {
  if (!isRecord(value)) return 'skipped';
  const link = value as unknown as LinkValue;
  if (!isSafeUrl(link.href)) return 'skipped';

  const tag = tagOf(el);
  if (tag !== 'a' && tag !== 'area' && tag !== 'link') return 'skipped';

  el.setAttribute('href', link.href);

  if (link.target) {
    el.setAttribute('target', link.target);
    if (link.target === '_blank') {
      const rel = link.rel ?? '';
      el.setAttribute(
        'rel',
        rel.indexOf('noopener') === -1 ? `${rel} noopener noreferrer`.trim() : rel,
      );
    } else if (link.rel) {
      el.setAttribute('rel', link.rel);
    }
  } else if (link.rel) {
    el.setAttribute('rel', link.rel);
  }

  if (tag === 'a' && link.text != null && el.getAttribute('data-siteflow-link-text') !== 'false') {
    el.textContent = link.text;
  }
  return 'applied';
}

function applyCanonical(el: Element, value: unknown): ApplyOutcome {
  if (typeof value !== 'string' || !isSafeUrl(value)) return 'skipped';
  const tag = tagOf(el);
  if (tag === 'link' || tag === 'a') {
    el.setAttribute('href', value);
    return 'applied';
  }
  writeScalar(el, value);
  return 'applied';
}

function applyJson(el: Element, value: unknown): ApplyOutcome {
  const path = el.getAttribute('data-siteflow-json-path');
  const attr = el.getAttribute(ATTR);
  // Dumping arbitrary JSON into a page is never what an author meant, so the
  // markup has to say exactly which slice it wants.
  if (!path && !attr) return 'skipped';

  const resolved = path ? getPath(value, path) : value;
  if (resolved == null) return 'skipped';

  writeScalar(el, typeof resolved === 'string' ? resolved : JSON.stringify(resolved));
  return 'applied';
}

/** Infers the field type of a repeater/group child from its markup or value. */
export function inferType(el: Element, value: unknown): FieldType {
  const declared = el.getAttribute('data-siteflow-type') as FieldType | null;
  if (declared) return declared;
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  if (Array.isArray(value)) return 'multiselect';
  if (isRecord(value)) {
    if ('href' in value) return 'link';
    if ('src' in value) return 'image';
    return 'group';
  }
  return 'text';
}

/** Fills `[data-siteflow-field]` descendants of a group or repeater item. */
export function fillFields(scope: ParentNode, item: unknown, ctx: ApplyContext): void {
  for (const fieldEl of Array.from(scope.querySelectorAll('[data-siteflow-field]'))) {
    const path = fieldEl.getAttribute('data-siteflow-field');
    if (!path) continue;
    const value = getPath(item, path);
    if (value == null) continue;
    try {
      applyValue(fieldEl, inferType(fieldEl, value), value, ctx);
    } catch {
      // One malformed child must never abort the rest of the item.
    }
  }
}

/**
 * Applies a single value to a single element.
 *
 * Returns `'skipped'` when the value does not fit the element, in which case the
 * original hand-authored markup is left exactly as it was.
 */
export function applyValue(
  el: Element,
  type: FieldType,
  value: unknown,
  ctx: ApplyContext,
): ApplyOutcome {
  let outcome: ApplyOutcome;

  switch (type) {
    case 'richtext':
      if (typeof value !== 'string') return 'skipped';
      replaceContent(el, sanitizeHtml(value));
      outcome = 'applied';
      break;

    case 'datetime':
      outcome = applyDatetime(el, value, ctx);
      break;

    case 'boolean':
      outcome = applyBoolean(el, value);
      break;

    case 'image':
    case 'og_image':
      outcome = applyImage(el, value);
      break;

    case 'file':
      outcome = applyFile(el, value);
      break;

    case 'link':
      outcome = applyLink(el, value);
      break;

    case 'canonical_url':
      outcome = applyCanonical(el, value);
      break;

    case 'multiselect':
      if (!Array.isArray(value)) return 'skipped';
      writeScalar(el, value.join(el.getAttribute('data-siteflow-separator') ?? ', '));
      outcome = 'applied';
      break;

    case 'json':
      outcome = applyJson(el, value);
      break;

    case 'group':
      if (!isRecord(value)) return 'skipped';
      fillFields(el, value, ctx);
      outcome = 'applied';
      break;

    default:
      // text, longtext, number, email, phone, color, select, seo_title,
      // seo_description and anything the server adds later.
      if (typeof value !== 'string' && typeof value !== 'number') return 'skipped';
      writeScalar(el, String(value));
      outcome = 'applied';
  }

  if (outcome === 'applied') el.setAttribute(STATE, 'applied');
  return outcome;
}
